import React from 'react';
import {
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import { optionToggleSx } from '../toggle-styles';
import { PatientInterval, PatientIntervals, PeriodBasis } from './types';
import { plural, toIntervalLabel } from './format';

interface Props {
  intervals: PatientIntervals;
  loading: boolean;
  basis: PeriodBasis;
  onBasisChange: (basis: PeriodBasis) => void;
  selectedIds: string[];
  onToggleInterval: (id: string) => void;
  dateFrom: Date | null;
  dateTo: Date | null;
  onDateFromChange: (d: Date | null) => void;
  onDateToChange: (d: Date | null) => void;
}

const BASES: { value: PeriodBasis; label: string }[] = [
  { value: 'stays', label: 'Whole Stay' },
  { value: 'coverage', label: 'Payer coverage' },
  { value: 'dates', label: 'Specific dates' },
];

/**
 * SQ-166, step 2 — WHEN.
 *
 * Replaces the Date range / Full episode / Payer period buttons with dated, counted,
 * selectable rows. One basis at a time (C3): stays and coverage describe different
 * things on the same timeline and a mixed selection would not mean anything. Within a
 * basis several rows may be ticked, which is why these are checkboxes and not radios (C2).
 */
export const PeriodBasisPicker: React.FC<Props> = ({
  intervals,
  loading,
  basis,
  onBasisChange,
  selectedIds,
  onToggleInterval,
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
}) => {
  const rows: PatientInterval[] = basis === 'coverage' ? intervals.coverage : intervals.stays;
  const emptyFor = (b: PeriodBasis) =>
    b === 'stays' ? intervals.stays.length === 0 : intervals.coverage.length === 0;

  return (
    <Box>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={basis}
        onChange={(_e, next) => next && onBasisChange(next)}
        sx={{ mb: 2, ...optionToggleSx }}
      >
        {BASES.map((b) => {
          // Empty is disabled, never hidden: a resident with one stay still sees
          // "Payer coverage" greyed out, which answers the question rather than
          // leaving them wondering where the option went (D8's spirit).
          const disabled = b.value !== 'dates' && !loading && emptyFor(b.value);
          const button = (
            <ToggleButton
              key={b.value}
              value={b.value}
              disabled={disabled}
            >
              {b.label}
            </ToggleButton>
          );
          return disabled ? (
            <Tooltip
              key={b.value}
              title={
                b.value === 'coverage'
                  ? 'No payer coverage periods recorded for this resident'
                  : 'No stays recorded for this resident'
              }
            >
              <span>{button}</span>
            </Tooltip>
          ) : (
            button
          );
        })}
      </ToggleButtonGroup>

      {basis === 'dates' ? (
        <Stack
          direction="row"
          spacing={2}
          sx={{ maxWidth: 460 }}
        >
          <DatePicker
            label="From"
            value={dateFrom}
            onChange={onDateFromChange}
            slotProps={{ textField: { size: 'small', fullWidth: true } }}
          />
          <DatePicker
            label="Through"
            value={dateTo}
            onChange={onDateToChange}
            slotProps={{ textField: { size: 'small', fullWidth: true } }}
          />
        </Stack>
      ) : (
        <Stack spacing={0.5}>
          {loading && (
            <Typography
              variant="body2"
              color="text.secondary"
            >
              Loading periods…
            </Typography>
          )}
          {!loading && rows.length === 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
            >
              No {basis === 'coverage' ? 'coverage periods' : 'stays'} recorded.
            </Typography>
          )}
          {rows.map((row) => (
            <FormControlLabel
              key={row.id}
              sx={{ alignItems: 'flex-start', ml: 0 }}
              control={
                <Checkbox
                  size="small"
                  checked={selectedIds.includes(row.id)}
                  onChange={() => onToggleInterval(row.id)}
                  inputProps={{ 'aria-label': toIntervalLabel(row.start, row.end) }}
                />
              }
              label={
                <Box sx={{ pt: 0.75 }}>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500 }}
                    >
                      {toIntervalLabel(row.start, row.end)}
                    </Typography>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={plural(row.documentCount, 'document')}
                    />
                    {row.spansStays && (
                      <Chip
                        size="small"
                        color="info"
                        variant="outlined"
                        label="spans more than one stay"
                      />
                    )}
                  </Stack>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                  >
                    {row.payerLabel || 'No payer recorded'}
                  </Typography>
                </Box>
              }
            />
          ))}
        </Stack>
      )}
    </Box>
  );
};
